import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Heart, MessageCircle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

const DISMISS_KEY = "ln_home_nudge_dismissed";
const DELAY_MS = 8500;

// Non-intrusive homepage nudge that suggests signup after ~8s of browsing.
// Auto-hides after dismissal for the whole session (sessionStorage).
export default function HomepageNudge() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (user) return;                      // never show to signed-in users
    if (location.pathname !== "/") return; // only on homepage
    if (sessionStorage.getItem(DISMISS_KEY) === "1") return;
    const t = setTimeout(() => setVisible(true), DELAY_MS);
    return () => clearTimeout(t);
  }, [user, loading, location.pathname]);

  const close = () => {
    setVisible(false);
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch {}
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-5 right-5 z-40 max-w-sm bg-[#FDFBF7] border border-[#1A1F2C]/15 shadow-2xl rounded-lg p-5 animate-in slide-in-from-bottom-6"
      role="dialog"
      aria-label="Sign up prompt"
      data-testid="home-nudge"
    >
      <button
        onClick={close}
        aria-label="Dismiss"
        className="absolute top-2.5 right-2.5 p-1 rounded hover:bg-[#F0EBE1]"
        data-testid="home-nudge-close"
      >
        <X size={16} />
      </button>
      <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[#C85A32]">
        <Sparkles size={13} /> Free to join
      </div>
      <div className="font-display text-xl mt-1.5">Sign up in 30 seconds</div>
      <p className="text-sm text-[#5C6B7A] mt-2 leading-relaxed">
        Save favourites, chat directly with owners, and see contact details on every listing.
      </p>
      <ul className="mt-3 space-y-1 text-xs text-[#1A1F2C]/80">
        <li className="flex items-center gap-1.5"><Heart size={12} className="text-[#C85A32]" /> Save any listing you like</li>
        <li className="flex items-center gap-1.5"><MessageCircle size={12} className="text-[#2B3A67]" /> Chat with owners instantly</li>
      </ul>
      <div className="mt-4 flex gap-2">
        <Button asChild size="sm" className="flex-1 bg-[#C85A32] hover:bg-[#a8482a] text-white rounded-md" data-testid="home-nudge-signup">
          <Link to="/register" onClick={close}>Sign up free</Link>
        </Button>
        <Button asChild size="sm" variant="outline" className="rounded-md" data-testid="home-nudge-login">
          <Link to="/login" onClick={close}>Log in</Link>
        </Button>
      </div>
    </div>
  );
}
